import React, { useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
import { useToast } from 'src/@/components/ui/use-toast';
import propostasService from 'src/services/propostasService';
import Config from 'src/config';

function getLinkPublico(publicId) {
  const base = Config.dev ? Config.url_dev_links : Config.url_prod_links;
  return `${base}/propostas/${publicId}`;
}

export default function CompartilharPropostaDialog({ open, onClose, proposta, temChat }) {
  const { toast } = useToast();
  const [emailDestino, setEmailDestino] = useState(proposta?.contato?.email || '');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  if (!proposta) return null;
  const link = getLinkPublico(proposta.publicId);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      propostasService.enviar(proposta._id, { canal: 'link' }).catch(() => {});
      toast({ title: 'Link copiado', description: 'O link da proposta foi copiado para a área de transferência' });
    } catch (err) {
      toast({ title: 'Erro', description: 'Não foi possível copiar o link', variant: 'destructive' });
    }
  };

  const handleSendEmail = async () => {
    if (!emailDestino.trim()) {
      toast({ title: 'Atenção', description: 'Informe o e-mail de destino', variant: 'destructive' });
      return;
    }
    setSendingEmail(true);
    try {
      const response = await propostasService.enviar(proposta._id, { canal: 'email', email: emailDestino.trim() });
      if (response.error) throw new Error(response.message);
      toast({ title: 'Sucesso', description: 'Proposta enviada por e-mail' });
    } catch (err) {
      toast({ title: 'Erro', description: err.message || 'Não foi possível enviar o e-mail', variant: 'destructive' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsapp = async () => {
    setSendingWhatsapp(true);
    try {
      const response = await propostasService.enviar(proposta._id, { canal: 'whatsapp' });
      if (response.error) throw new Error(response.message);
      toast({ title: 'Sucesso', description: 'Proposta enviada pelo WhatsApp' });
    } catch (err) {
      toast({ title: 'Erro', description: err.message || 'Não foi possível enviar pelo WhatsApp', variant: 'destructive' });
    } finally {
      setSendingWhatsapp(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      // Assinada: a via que interessa é a que traz o certificado de assinatura.
      let url = proposta.pdf_assinado_url || proposta.pdf_url;
      if (!url) {
        const response = await propostasService.gerarPdf(proposta._id);
        if (response.error) throw new Error(response.message);
        url = response.data.pdf_url;
      }
      window.open(url, '_blank');
    } catch (err) {
      toast({ title: 'Erro', description: err.message || 'Não foi possível gerar o PDF', variant: 'destructive' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[90vw] w-[420px] rounded-2xl border-0 shadow-2xl dark:bg-[#222529] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-lg font-bold text-gray-900 dark:text-[#D1D2D3]">Compartilhar proposta</DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-2 space-y-2 overflow-hidden">
          <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-[#383838] overflow-hidden">
            <div className="w-9 h-9 rounded-lg bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center flex-shrink-0">
              <Iconify icon="mdi:link-variant" width={18} className="text-[#7C3AED]" />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">Compartilhar link da proposta</p>
              <p className="text-xs text-gray-500 dark:text-[#ABABAD] truncate">Copie e compartilhe o link</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-lg flex-shrink-0" onClick={handleCopyLink}>
              Copiar
            </Button>
          </div>

          {temChat && (
            <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-[#383838] overflow-hidden">
              <div className="w-9 h-9 rounded-lg bg-green-50 dark:bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <Iconify icon="ic:round-whatsapp" width={18} className="text-green-600" />
              </div>
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">Enviar pelo WhatsApp</p>
                <p className="text-xs text-gray-500 dark:text-[#ABABAD] truncate">Envia o link no chat vinculado</p>
              </div>
              <Button size="sm" variant="outline" className="rounded-lg flex-shrink-0" onClick={handleSendWhatsapp} disabled={sendingWhatsapp}>
                {sendingWhatsapp ? <Iconify icon="mdi:loading" className="animate-spin" width={14} /> : 'Enviar'}
              </Button>
            </div>
          )}

          <div className="p-3 rounded-xl border border-gray-100 dark:border-[#383838] overflow-hidden">
            <div className="flex items-center gap-3 mb-2 overflow-hidden">
              <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Iconify icon="mdi:email-outline" width={18} className="text-blue-600" />
              </div>
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">Enviar proposta por email</p>
                <p className="text-xs text-gray-500 dark:text-[#ABABAD] truncate">Envie o link por e-mail</p>
              </div>
            </div>
            <div className="flex items-center gap-2 pl-12">
              <Input
                value={emailDestino}
                onChange={e => setEmailDestino(e.target.value)}
                placeholder="email@cliente.com"
                className="h-8 text-sm rounded-lg min-w-0 flex-1"
              />
              <Button size="sm" variant="outline" className="rounded-lg flex-shrink-0" onClick={handleSendEmail} disabled={sendingEmail}>
                {sendingEmail ? <Iconify icon="mdi:loading" className="animate-spin" width={14} /> : 'Enviar'}
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-[#383838] overflow-hidden">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${proposta.pdf_assinado_url ? 'bg-green-50 dark:bg-green-500/10' : 'bg-gray-50 dark:bg-gray-500/10'}`}>
              <Iconify
                icon={proposta.pdf_assinado_url ? 'mdi:draw-pen' : 'mdi:file-pdf-box'}
                width={18}
                className={proposta.pdf_assinado_url ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-[#ABABAD]'}
              />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">
                {proposta.pdf_assinado_url ? 'Baixar PDF assinado' : 'Baixar arquivo PDF'}
              </p>
              <p className="text-xs text-gray-500 dark:text-[#ABABAD] truncate">
                {proposta.pdf_assinado_url ? 'Inclui o certificado de assinatura' : 'Baixe a proposta em PDF'}
              </p>
            </div>
            <Button size="sm" variant="outline" className="rounded-lg flex-shrink-0" onClick={handleDownloadPdf} disabled={downloadingPdf}>
              {downloadingPdf ? <Iconify icon="mdi:loading" className="animate-spin" width={14} /> : 'Baixar'}
            </Button>
          </div>
        </div>

        <div className="px-6 py-4 text-center">
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-[#D1D2D3] transition-colors">
            Fechar e compartilhar depois
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
